/**
 * VideoCallVideoViews — the live video surface of the 1:1 chat video-call page.
 *
 * Web port of the Flutter `_videoView` / `_animatedLocalVideoView` /
 * `_animatedRemoteVideoView` widgets (`video_call_screen.dart:863-1035`), mapped
 * onto `agora-rtc-react`'s declarative video components:
 *   - the remote (peer) user fills the main viewport via `<RemoteUser>`;
 *   - the local camera track shows as a small top-right corner PiP via
 *     `<LocalVideoTrack>`.
 *
 * The remote user is selected by uid — Flutter renders the remote canvas for
 * `uid = isCaller ? audienceStreamingId : callerUserStreamingId`
 * (`video_call_screen.dart:1023-1027`); this component picks the matching entry
 * out of the hook's `remoteUsers`.
 *
 * Tap-to-clear-screen: a tap on the large main view toggles the hook's
 * `clearScreen` state (Flutter `_clearScreen`, `:955`/`:1012`). When cleared the
 * PiP is hidden (Flutter collapses it to `1×1`, `:941-1006`) and the page's
 * bottom controls hide (Flutter `_bottomWidget` returns empty, `:1113`). The
 * page reads the same `clearScreen` flag to gate its controls / back arrow.
 *
 * Tap-to-swap: tapping the PiP corner view swaps which stream fills the main
 * view (Flutter's PiP `onTap` triggers a fullscreen swap of the two canvases,
 * `:963-974`). The web ports this as a *static* (non-animated) swap driven by
 * local `localInMain` state — the PiP tap stops propagation so it no longer
 * falls through to the clear-screen handler.
 *
 * **Deviations from plan:** the elaborate Flutter fullscreen-swap *animation*
 * (`_animatedLocalVideoView` / `_animatedRemoteVideoView` with the
 * local-on-top ↔ remote-on-top `AnimatedPositioned`/`AnimatedContainer`
 * transition, `:917-1035`) is intentionally NOT ported. Phase 1 uses the
 * simplified static main+PiP layout authorised by task-prompt point 6 ("a static
 * main+PiP layout is acceptable") and the `conventions.md` UI-deviation rule —
 * the swap itself is implemented statically (an instant stream switch, no
 * `AnimatedPositioned` transition; that animation stays the deferred Phase-2
 * follow-up below).
 * Web PiP sizing also deviates from Flutter's phone-tuned
 * `videoCallRemoteViewWidth = 80` 4:3 box (it scales up per `responsive.videoCall`
 * for the desktop viewport) — a sanctioned UI sizing deviation. Tap-to-clear is
 * kept simple (a single boolean toggle, no animation).
 *
 * Theme/responsive/l10n + `qaAttr` are mandatory (`presentation.md`). The video
 * containers carry no user-visible copy, so l10n is consumed as the localized
 * accessibility `aria-label` on each region.
 */

import React, { useMemo, useState } from 'react';
import { RemoteUser, LocalVideoTrack } from 'agora-rtc-react';
import type { IAgoraRTCRemoteUser, ICameraVideoTrack } from 'agora-rtc-react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';

export interface VideoCallVideoViewsProps {
  /** `_isCaller` — selects which remote uid is the peer (`:1024-1026`). */
  isCaller: boolean;
  /** The remote users currently in the channel (from `useRemoteUsers()`). */
  remoteUsers: IAgoraRTCRemoteUser[];
  /** The audience's streaming id (the remote uid when this side is the caller, `:1025`). */
  audienceStreamingId: number | null;
  /** The caller's streaming id (the remote uid when this side is the audience, `:1026`). */
  callerUserStreamingId?: number;
  /** The local camera track for the PiP self-view (Flutter local `VideoCanvas(uid: 0)`, `:965`). */
  localCameraTrack: ICameraVideoTrack | null;
  /** Whether the camera is off (PiP shows a placeholder rather than the track). */
  isCameraOff: boolean;
  /** `_clearScreen` — when true the PiP is hidden (Flutter collapses it to 1×1, `:941-1006`). */
  clearScreen: boolean;
  /** Toggle `clearScreen` (Flutter tap handler, `:955`/`:1012`). */
  onToggleClearScreen: () => void;
}

export const VideoCallVideoViews: React.FC<VideoCallVideoViewsProps> = ({
  isCaller,
  remoteUsers,
  audienceStreamingId,
  callerUserStreamingId,
  localCameraTrack,
  isCameraOff,
  clearScreen,
  onToggleClearScreen,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Static view swap (Flutter's PiP fullscreen-swap, `video_call_screen.dart:963-974`).
  // `false` (default) = remote peer fills the main view, local camera is the PiP
  // (today's layout); `true` = local camera fills the main view, remote peer
  // renders in the PiP. Component-local: no sibling/page consumer reads it (the
  // page gates overlays purely on `clearScreen`).
  const [localInMain, setLocalInMain] = useState(false);

  // The peer's uid — Flutter `uid = isCaller ? audienceStreamingId
  // : callerUserStreamingId` (`video_call_screen.dart:1023-1027`).
  const remoteUid = isCaller ? audienceStreamingId : callerUserStreamingId;

  // Pick the matching remote user out of the channel members. `uid` is a
  // `UID = string | number` on the SDK, so compare loosely on the numeric value.
  const remoteUser = useMemo(() => {
    if (remoteUid == null) {
      return undefined;
    }
    return remoteUsers.find((user) => Number(user.uid) === Number(remoteUid));
  }, [remoteUsers, remoteUid]);

  const pipWidth = responsive.videoCall.pipWidth;
  const pipHeight = pipWidth * CONTENT_CONSTANTS.videoCallPipAspectRatio;

  // Remote (peer) stream — Flutter `_animatedRemoteVideoView`
  // `AgoraVideoView.remote` (`:1019-1030`). `<RemoteUser>` mounts/plays the
  // peer's video + audio into a managed full-bleed container. Audio is always
  // played regardless of which surface it occupies.
  //
  // Memoized on `[remoteUser]` only so a *local* mic/camera toggle
  // (`isMute` / `isCameraOff`) does NOT rebuild this element — without the
  // memo a new `<RemoteUser>` is produced every render and React remounts the
  // underlying Agora video DOM, which flickers the peer view (web-only defect;
  // Flutter's `AgoraVideoView` retains element identity across rebuilds). The
  // stable `key` keeps the same DOM video element mounted when the stream moves
  // between the main slot and the PiP slot on a `localInMain` swap.
  const remoteStream = useMemo(
    () =>
      remoteUser && (
        <RemoteUser
          key="remote-stream"
          user={remoteUser}
          playVideo
          playAudio
          videoPlayerConfig={{ fit: 'contain' }}
          style={{ width: '100%', height: '100%' }}
        />
      ),
    [remoteUser],
  );

  // Local self-view stream — Flutter `_animatedLocalVideoView`
  // `AgoraVideoView` `VideoCanvas(uid: 0)` (`:962-967`). `<LocalVideoTrack play>`
  // mounts the camera track into its managed container. When the camera is off
  // the track is disabled, so the `theme.surface` backdrop shows through
  // (Flutter shows the muted canvas, `:1280-1312`).
  //
  // Memoized on `[localCameraTrack, isCameraOff]` — the local view legitimately
  // reacts to the camera toggle (it must show/hide the track) but should not be
  // rebuilt by unrelated re-renders (e.g. a mic mute). The stable `key` keeps
  // the same DOM element mounted across a main/PiP swap.
  const localStream = useMemo(
    () => (
      <LocalVideoTrack
        key="local-stream"
        track={localCameraTrack}
        play={!isCameraOff}
        style={{ width: '100%', height: '100%' }}
      />
    ),
    [localCameraTrack, isCameraOff],
  );

  // `localInMain` selects which stream fills the main view vs. the PiP corner.
  const mainStream = localInMain ? localStream : remoteStream;
  const pipStream = localInMain ? remoteStream : localStream;

  const swapViews = () => setLocalInMain((prev) => !prev);

  return (
    // Main tap surface — a tap toggles clear-screen (Flutter main-view
    // `GestureDetector` `onTap` toggles `_clearScreen`, `:1011-1012`/`:1025`).
    // The selected main stream fills it; the PiP overlays the top-right corner.
    <div
      {...qaAttr({ id: 'video-call-remote-view' })}
      onClick={onToggleClearScreen}
      aria-label={l10n.chat.videoCallPage.remoteVideoLabel}
      style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: theme.background,
        cursor: 'pointer',
        overflow: 'hidden',
      }}
    >
      {mainStream}

      {/* PiP corner view — Flutter `_animatedLocalVideoView` /
          `_animatedRemoteVideoView` PiP (`:930-985`). Top-right corner,
          `responsive`-sized (web sizing deviation, see file header). Hidden when
          `clearScreen` (Flutter collapses it to `1×1`, `:941-949`). Tapping it
          statically swaps which stream fills the main view (Flutter's PiP
          fullscreen-swap tap, `:963-974`) and stops propagation so it does NOT
          fall through to the clear-screen handler.
          // TODO: @claude add a follow up task for this: animated PiP
          // fullscreen-swap — Flutter video_call_screen.dart:917-1035 */}
      {!clearScreen && (
        <div
          {...qaAttr({ id: 'video-call-local-view' })}
          role="button"
          tabIndex={0}
          aria-label={l10n.chat.videoCallPage.swapVideoLabel}
          onClick={(event) => {
            event.stopPropagation();
            swapViews();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              swapViews();
            }
          }}
          style={{
            position: 'absolute',
            top: `calc(${responsive.safeArea.top} + ${responsive.videoCall.pipInset}px)`,
            right: responsive.videoCall.pipInset,
            width: pipWidth,
            height: pipHeight,
            borderRadius: responsive.videoCall.pipBorderRadius,
            overflow: 'hidden',
            backgroundColor: theme.surface,
            border: `${responsive.border.thin}px solid ${theme.border}`,
            cursor: 'pointer',
            zIndex: 5,
          }}
        >
          {pipStream}
        </div>
      )}
    </div>
  );
};
